import React from "react"
import { Button, ButtonProps, ResponsiveValue } from "@chakra-ui/react"

import Icon from "@app/components/Icon"
import { carouselArrowDisabledState } from "@app/theme/components/Button"

type Direction = "prev" | "next"
type ChevronName = "carouselChevron" | "carouselChevronStroke" | "carouselChevronPair" | "chevron"
type ChevronIcon = { name: string; fill?: string; transform?: string }

// carouselChevron's sprite symbol carries no fill, so fill="none" keeps the glyph stroked rather than filled.
// Its stroke is currentColor and is left uncoloured here so it follows the button's own colour - brand grey
// while enabled, the disabled arrow grey once Swiper marks the arrow disabled.
const chevrons: Record<ChevronName, Record<Direction, ChevronIcon>> = {
  carouselChevron: {
    prev: { name: "carouselChevron", fill: "none", transform: "rotate(180deg)" },
    next: { name: "carouselChevron", fill: "none" },
  },
  // Same glyph as carouselChevron, but its stroke doesn't scale with the icon and takes its width from the
  // inherited stroke-width, so one icon can shrink on mobile and keep a fine line
  carouselChevronStroke: {
    prev: { name: "carouselChevronStroke", fill: "none", transform: "rotate(180deg)" },
    next: { name: "carouselChevronStroke", fill: "none" },
  },
  carouselChevronPair: {
    prev: { name: "carouselChevronLeft" },
    next: { name: "carouselChevronRight" },
  },
  chevron: {
    prev: { name: "chevronLeft" },
    next: { name: "chevronLeft", transform: "rotate(180deg)" },
  },
}

// Swiper toggles a class on the nav element when no further slides exist - "swiper-disabled" everywhere in
// this codebase, "swiper-button-disabled" when a caller leaves Swiper's default. Only the circle arrows carry
// that treatment here; the borderless carousels each style (or deliberately ignore) the class on their own
// wrapper, and a rule here would override them. isDisabled-driven callers (manual pagers) fade via _disabled.
const circleDisabledStyles = { ...carouselArrowDisabledState, cursor: "auto", pointerEvents: "none" }
const chevronDisabledStyles = { opacity: 0.5, cursor: "auto", pointerEvents: "none" }

const circleStyles: ButtonProps = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxSize: "48px",
  minW: "48px",
  p: "0",
  borderRadius: "full",
  border: "1px solid",
  borderColor: "border.lightest",
  bg: "primary.white",
  color: "primary.midGrey",
  sx: { "&.swiper-disabled, &.swiper-button-disabled": circleDisabledStyles },
}

type Props = {
  direction: Direction
  label: string
  circle?: boolean
  icon?: ChevronName
  iconSize?: ResponsiveValue<string>
} & ButtonProps

const CarouselButton: React.FC<Props> = ({ direction, label, circle, icon = "carouselChevron", iconSize = "14px", ...props }) => (
  <Button
    aria-label={label}
    _after={{ content: '""', fontSize: "0 !important" }}
    _disabled={circle ? circleDisabledStyles : chevronDisabledStyles}
    {...(circle ? circleStyles : {})}
    {...props}
  >
    <Icon {...chevrons[icon][direction]} width={iconSize} height={iconSize} />
  </Button>
)

export default React.memo(CarouselButton)
